import { highlightSearchText } from "@/utils/textHelpers";

// Самый длинный номер — вьетнамский городской с кодом страны: 84 + 10 цифр
const MAX_PHONE_DIGITS = 12;

// Кандидат в номер — цифры с разделителями между ними, номер или нет решает isPhoneNumber.
// Повторения ограничены везде, где после них возможен откат: иначе длинный токен
// без пробелов разбирается квадратично или хуже.
const PHONE_REG_EXP = /\+?\(?\d(?:[\s().-]{0,3}\d)*/
const EMAIL_REG_EXP = /[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*/
const URL_REG_EXP = /(?:(?:https?|ftp):\/\/)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.){1,10}[a-zA-Z]{2,}(?::\d{1,5})?(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#[^\s]*)?|(?:(?:https?|ftp):\/\/)?\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(?::\d{1,5})?(?:\/[^\s?#]*)?|localhost(?::\d{1,5})?(?:\/[^\s?#]*)?/

// Email раньше телефона: цифры перед @ — часть адреса, а не номер.
const COMBINED_REG_EXP = new RegExp(
    `(?<email>${EMAIL_REG_EXP.source})|(?<phone>${PHONE_REG_EXP.source})|(?<url>${URL_REG_EXP.source})`,
    'gi'
);

type TextSegment = {
    text: string;
    isPhone: boolean;
    isEmail: boolean;
    isUrl: boolean;
};

type TextRange = {
    start: number;
    end: number;
};

// Номера России и Вьетнама: длина и начальные цифры, с префиксом +7 / +84 или без
export function isPhoneNumber(digits: string, hasPlus: boolean): boolean {
    if (hasPlus) {
        if (digits.startsWith('7')) {
            return digits.length === 11;
        }
        if (digits.startsWith('84')) {
            return digits.length === 11 || (digits.length === 12 && digits[2] === '2');
        }
        return false;
    }
    switch (digits.length) {
        case 10:
            return /^(?:[34589]|0[35789])/.test(digits);
        case 11:
            return /^(?:[78]|02)/.test(digits);
        case 12:
            return digits.startsWith('842');
        default:
            return false;
    }
}

// Номера внутри кандидата. Режем только между группами цифр, чтобы не выделять номер
// из середины длинного числа, и выбираем разбиение с наибольшим числом номеров:
// best[i] — номеров в первых i группах, phoneStart[i] — первая группа номера,
// который заканчивается группой i - 1 (-1, если она в номер не входит).
function findPhoneRanges(candidate: string): TextRange[] {
    const datePattern = /(?:^|[^\d])((?:0?[1-9]|[12]\d|3[01])([./-])(?:0?[1-9]|1[0-2])\2\d{4})(?!\d)/g;
    const ranges: TextRange[] = [];
    let offset = 0;
    const appendRanges = (end: number) => {
        for (const range of findPhoneRangesWithoutDates(candidate.slice(offset, end))) {
            ranges.push({ start: range.start + offset, end: range.end + offset });
        }
    };
    for (const match of candidate.matchAll(datePattern)) {
        const dateStart = (match.index ?? 0) + match[0].length - match[1].length;
        appendRanges(dateStart);
        offset = dateStart + match[1].length;
    }
    appendRanges(candidate.length);
    return ranges;
}

function findPhoneRangesWithoutDates(candidate: string): TextRange[] {
    const groups: TextRange[] = [];
    const groupRegExp = /\d+/g;
    let group: RegExpExecArray | null;
    while ((group = groupRegExp.exec(candidate)) !== null) {
        groups.push({ start: group.index, end: groupRegExp.lastIndex });
    }

    const hasPlus = candidate.startsWith('+');
    const best = new Array<number>(groups.length + 1).fill(0);
    const phoneStart = new Array<number>(groups.length + 1).fill(-1);

    for (let i = 0; i < groups.length; i++) {
        if (best[i] > best[i + 1]) {
            best[i + 1] = best[i];
            phoneStart[i + 1] = -1;
        }
        let digits = '';
        for (let j = i; j < groups.length; j++) {
            digits += candidate.slice(groups[j].start, groups[j].end);
            if (digits.length > MAX_PHONE_DIGITS) {
                break;
            }
            if (best[i] + 1 > best[j + 1] && isPhoneNumber(digits, hasPlus && i === 0)) {
                best[j + 1] = best[i] + 1;
                phoneStart[j + 1] = i;
            }
        }
    }

    const ranges: TextRange[] = [];
    let groupCount = groups.length;
    while (groupCount > 0) {
        const first = phoneStart[groupCount];
        if (first < 0) {
            groupCount--;
            continue;
        }
        // Первый номер забирает и префикс кандидата («+», «(»), следующие — открывающую скобку
        let start = first === 0 && hasPlus ? 0 : groups[first].start;
        if (candidate[start - 1] === '(') {
            start--;
        }
        const end = groups[groupCount - 1].end;
        if (candidate[start] === '(') {
            const closingBracket = candidate.indexOf(')', start);

            if (closingBracket === -1 || closingBracket >= end) {
                start++;
            }
        }
        ranges.push({ start, end });
        groupCount = first;
    }
    return ranges.reverse();
}

// Обычный текст дописываем к предыдущему обычному сегменту. Он остаётся сырым:
// подсвечивает и экранирует его вызывающий код.
function appendPlainText(result: TextSegment[], text: string) {
    if (!text) {
        return;
    }
    const last = result[result.length - 1];
    if (last && !last.isPhone && !last.isEmail && !last.isUrl) {
        last.text += text;
    } else {
        result.push({ text, isPhone: false, isEmail: false, isUrl: false });
    }
}

export function splitTextByPhoneNumbers(input: string, searchText: string) {
    const replacedText = input.replace(/<span class="searchedText">|<\/span>/g, '')
    const result: TextSegment[] = [];
    let lastIndex = 0;

    const combinedRegExp = COMBINED_REG_EXP;
    combinedRegExp.lastIndex = 0;

    let match: RegExpExecArray | null;

    while ((match = combinedRegExp.exec(replacedText)) !== null) {
        const matchedText = match[0];
        appendPlainText(result, replacedText.substring(lastIndex, match.index));
        lastIndex = combinedRegExp.lastIndex;

        if (match.groups?.phone !== undefined) {
            let phoneEnd = 0;
            for (const { start, end } of findPhoneRanges(matchedText)) {
                appendPlainText(result, matchedText.slice(phoneEnd, start));
                result.push({
                    text: highlightSearchText(searchText, matchedText.slice(start, end)),
                    isPhone: true,
                    isEmail: false,
                    isUrl: false
                });
                phoneEnd = end;
            }
            appendPlainText(result, matchedText.slice(phoneEnd));
            continue;
        }

        const isEmail = match.groups?.email !== undefined;
        const isUrl = !isEmail && isValidUrl(matchedText);
        if (isEmail || isUrl) {
            result.push({
                text: highlightSearchText(searchText, matchedText),
                isPhone: false,
                isEmail,
                isUrl
            });
        } else {
            appendPlainText(result, matchedText);
        }
    }

    appendPlainText(result, replacedText.substring(lastIndex));

    if (result.length === 0) {
        result.push({
            text: replacedText,
            isPhone: false,
            isEmail: false,
            isUrl: false
        });
    }

    return result;
}

// Вспомогательная функция для валидации URL
function isValidUrl(text: string): boolean {
    try {
        let urlText = text;
        if (!/^(https?|ftp):\/\//i.test(text)) {
            urlText = 'http://' + text;
        }

        const url = new URL(urlText);

        const domainParts = url.hostname.split('.');
        if (domainParts.length < 2) return false;

        const tld = domainParts[domainParts.length - 1].toLowerCase();
        const validTlds = [
            'com', 'org', 'net', 'edu', 'gov', 'mil', 'io', 'co',
            'ai', 'ru', 'ua', 'by', 'kz', 'uk', 'de', 'fr', 'es',
            'info', 'biz', 'me', 'tv', 'app', 'dev', 'online'
        ];

        if (tld.length < 2 || (!validTlds.includes(tld) && !/^[a-z]{2,}$/.test(tld))) {
            return false;
        }

        if (!/^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(url.hostname)) {
            return false;
        }

        return true;
    } catch (error) {
        return false;
    }
}
